import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TIMING = {
  counting: 2800,
  hold: 500,
  crossfade: 400,
  panelSplit: 1200,
  logoFade: 800,
};

const EASE_MAJESTIC = [0.4, 0, 0.2, 1];

const Preloader = ({ onComplete }) => {
  const [count, setCount] = useState(0);
  const [phase, setPhase] = useState('counting');
  // phases: counting → hold → crossfade → split → done

  const finish = useCallback(() => onComplete(), [onComplete]);

  /* ── Counter 0→100 with eased progression ── */
  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - start) / TIMING.counting, 1);
      const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      setCount(Math.round(eased * 100));
      if (t < 1) { raf = requestAnimationFrame(tick); }
      else { setPhase('hold'); }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  /* ── Phase state machine ── */
  useEffect(() => {
    let t;
    switch (phase) {
      case 'hold':
        t = setTimeout(() => setPhase('crossfade'), TIMING.hold);
        break;
      case 'crossfade':
        t = setTimeout(() => setPhase('split'), TIMING.crossfade);
        break;
      case 'split':
        t = setTimeout(() => { setPhase('done'); finish(); }, TIMING.panelSplit);
        break;
      default: break;
    }
    return () => clearTimeout(t);
  }, [phase, finish]);

  const isSplitting = phase === 'split' || phase === 'done';
  const showGold = phase === 'crossfade' || isSplitting;

  return (
    <AnimatePresence>
      {phase !== 'done' && (
        <div className="fixed inset-0" style={{ zIndex: 9999 }} data-testid="preloader">

          {/* ══ LEFT PANEL ══ */}
          <motion.div
            className="absolute top-0 left-0 h-full"
            style={{ width: '50vw', backgroundColor: '#062C1F' }}
            animate={{ x: isSplitting ? '-100%' : '0%' }}
            transition={{ duration: TIMING.panelSplit / 1000, ease: EASE_MAJESTIC }}
          >
            {/* Subtle inner gradient */}
            <div className="absolute inset-0" style={{
              background: 'linear-gradient(135deg, rgba(196,167,108,0.03) 0%, transparent 60%)',
            }} />
          </motion.div>

          {/* ══ RIGHT PANEL ══ */}
          <motion.div
            className="absolute top-0 right-0 h-full"
            style={{ width: '50vw', backgroundColor: '#062C1F' }}
            animate={{ x: isSplitting ? '100%' : '0%' }}
            transition={{ duration: TIMING.panelSplit / 1000, ease: EASE_MAJESTIC }}
          >
            <div className="absolute inset-0" style={{
              background: 'linear-gradient(225deg, rgba(196,167,108,0.03) 0%, transparent 60%)',
            }} />
          </motion.div>

          {/* ══ CENTER CONTENT (on top of panels) ══ */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ zIndex: 10 }}>

            {/* ── Logo container ── */}
            <motion.div
              className="relative"
              style={{ width: '230px', height: '230px' }}
              animate={isSplitting ? { scale: 0.7, opacity: 0 } : { scale: 1, opacity: 1 }}
              transition={{
                duration: TIMING.logoFade / 1000,
                ease: EASE_MAJESTIC,
              }}
            >
              {/* Ghost outline (always visible, very dim) */}
              <img
                src="/assets/logos/logo-blanc.png"
                alt=""
                className="absolute inset-0 w-full h-full object-contain"
                style={{ opacity: 0.06, imageRendering: 'high-quality' }}
                draggable={false}
              />

              {/* White logo with clip-path reveal */}
              <motion.img
                src="/assets/logos/logo-blanc.png"
                alt="One Green Dev"
                className="absolute inset-0 w-full h-full object-contain"
                style={{
                  clipPath: `inset(${100 - count}% 0 0 0)`,
                  imageRendering: 'high-quality',
                }}
                animate={{ opacity: showGold ? 0 : 1 }}
                transition={{ duration: TIMING.crossfade / 1000, ease: 'easeOut' }}
                draggable={false}
              />

              {/* Gold logo (cross-fade in) */}
              <motion.img
                src="/assets/logos/logo-dore.png"
                alt="One Green Dev"
                className="absolute inset-0 w-full h-full object-contain"
                style={{ imageRendering: 'high-quality' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: showGold ? 1 : 0 }}
                transition={{ duration: TIMING.crossfade / 1000, ease: 'easeIn' }}
                draggable={false}
              />
            </motion.div>

            {/* ── Counter ── */}
            <motion.div
              className="mt-10 flex items-baseline"
              animate={isSplitting ? { opacity: 0, y: 10 } : { opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <span
                className="text-5xl sm:text-6xl font-light tabular-nums"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  color: 'rgba(196,167,108,0.8)',
                  letterSpacing: '0.04em',
                }}
              >
                {count}
              </span>
              <span
                className="text-lg sm:text-xl ml-1"
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  color: 'rgba(196,167,108,0.35)',
                  fontWeight: 300,
                }}
              >
                %
              </span>
            </motion.div>

            {/* ── Progress line ── */}
            <motion.div
              className="mt-8"
              style={{ width: '140px' }}
              animate={isSplitting ? { opacity: 0 } : { opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              <div className="h-px w-full" style={{ backgroundColor: 'rgba(196,167,108,0.1)' }} />
              <motion.div
                className="h-px"
                style={{
                  backgroundColor: '#c4a76c',
                  width: `${count}%`,
                  boxShadow: '0 0 8px rgba(196,167,108,0.25)',
                  marginTop: '-1px',
                }}
              />
            </motion.div>

            {/* ── Tagline ── */}
            <motion.p
              className="mt-6 text-[10px] sm:text-xs tracking-[0.3em] uppercase"
              style={{
                fontFamily: "'Montserrat', sans-serif",
                color: 'rgba(255,255,255,0.2)',
              }}
              initial={{ opacity: 0, y: 6 }}
              animate={{
                opacity: count > 50 && !isSplitting ? 1 : 0,
                y: count > 50 ? 0 : 6,
              }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              Where territory becomes destination
            </motion.p>
          </div>

          {/* ══ SEAM LINE (center divider between panels) ══ */}
          <motion.div
            className="absolute top-0 h-full"
            style={{
              left: '50%',
              width: '1px',
              background: 'linear-gradient(to bottom, transparent 10%, rgba(196,167,108,0.08) 50%, transparent 90%)',
              zIndex: 5,
            }}
            animate={{ opacity: isSplitting ? 0 : 1 }}
            transition={{ duration: 0.15 }}
          />
        </div>
      )}
    </AnimatePresence>
  );
};

export default Preloader;
